import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { getListingTimezoneSync } from './listingTimezone';

// Initialize dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const DEFAULT_TIMEZONE = 'America/Chicago';

/**
 * What the closing-date helpers below read.
 *
 * `deadlines` is the materialized contract schedule, and its CLOSING row
 * outranks `offer.closingDate`. The base `offers` row is not the deal's closing
 * date once anyone has countered: a counter writes its negotiated closing date
 * onto the accepted `offerRounds` row and never back onto the offer, and the
 * frontend does not even fetch `offer.currentRound`. The backend already
 * resolves that precedence when it materializes the schedule
 * (`buildDeadlineInput` in transaction-schedule.ts), so reading the CLOSING
 * deadline is how the page inherits it. Reading the offer directly is what left
 * the header showing the pre-counter date while the schedule card below it
 * showed the real one.
 */
interface ClosingDateSource {
  offer?: { closingDate?: string | Date | null } | null;
  /** Contract calendar date; distinct from a confirmed closing appointment. */
  closingDate?: string | Date | null;
  closingTime?: string | Date | null;
  deadlines?: { type: string; deadlineDate: string | Date }[] | null;
  listings?: { state?: string | null } | null;
  /** `transactions.closing` — the only real signal a deal actually closed; a
   * passed closing date alone never means that (see isTransactionStaleClosed). */
  closing?: string | null;
}

/** Return the best transaction-level closing value for lightweight admin surfaces. */
export const getTransactionClosingValue = (
  transaction: ClosingDateSource | null | undefined
): string | Date | null =>
  transaction?.closingDate ?? transaction?.closingTime ?? transaction?.offer?.closingDate ?? null;

/**
 * Contract dates belong to the property, not the reader — the same rule
 * TransactionSchedule.tsx follows. Both the CLOSING deadline and the offer's
 * closing date are stored as 23:59:59.999 on the closing day in the property's
 * timezone, so a viewer east of the house reads the next calendar day unless
 * it is converted back. Falls back to the caller's timezone only when there is
 * no listing on the record to resolve a state from.
 *
 * `propertyTimezone`, when supplied, is the coordinate-resolved zone from
 * `usePropertyTimezone`/`getListingTimezone` and always wins over the
 * state-code map below: Tennessee spans Eastern and Central, so a
 * Knoxville property mapped by state alone reads as Central and stores a
 * picked 2pm closing an hour off. The state map only remains the answer when
 * no coordinate-resolved zone has been supplied — e.g. the async tz-lookup
 * hasn't resolved yet, or the listing has no lat/lng.
 */
const resolveClosingTimezone = (
  transaction: ClosingDateSource | null | undefined,
  fallback: string,
  propertyTimezone?: string
): string =>
  propertyTimezone ??
  (transaction?.listings
    ? getListingTimezoneSync(transaction.listings)
    : fallback);

/** The closing day as `YYYY-MM-DD`, from the schedule if it has one. */
const resolveClosingDay = (
  transaction: ClosingDateSource | null | undefined,
  propertyTimezone: string
): string | null => {
  const scheduled = transaction?.deadlines?.find(
    (deadline) => deadline.type === 'CLOSING'
  );
  if (scheduled?.deadlineDate) {
    return dayjs(scheduled.deadlineDate)
      .tz(propertyTimezone)
      .format('YYYY-MM-DD');
  }
  if (transaction?.closingDate) {
    return dayjs(transaction.closingDate)
      .tz(propertyTimezone)
      .format('YYYY-MM-DD');
  }
  if (transaction?.offer?.closingDate) {
    return dayjs(transaction.offer.closingDate).utc().format('YYYY-MM-DD');
  }
  return null;
};

/**
 * Where a closing time came from, which decides how its clock is read.
 *
 * `persisted` is an instant off the API (`timestamptz`). Its clock has to be
 * read back in the property's zone: a 2pm Central closing is stored as 19:00Z,
 * so reading it in the browser's zone yields 3pm for an Eastern viewer and 7pm
 * on a UTC box.
 *
 * `picked` is a Date the time picker just produced, whose *local* clock fields
 * are the wall-clock time the user chose. Converting it would shift the very
 * value they picked.
 */
type ClosingTimeSource = 'persisted' | 'picked';

/**
 * Pins a `YYYY-MM-DD` closing day to a wall-clock time in a given zone.
 *
 * `dayjs.tz(string, zone)`, never `dayjs(string, zone)`. dayjs reads that
 * second positional argument as a parse format, and `customParseFormat` is not
 * registered anywhere in this app — so the zone was silently dropped and the
 * closing time was built in whatever zone the reader's browser happened to be
 * in. That is how a 2pm Central closing read as 2pm Eastern in Atlanta.
 */
const atClosingTime = (
  closingDay: string,
  closingTime: string | Date | null | undefined,
  zone: string,
  source: ClosingTimeSource
) => {
  // With no stated time, noon — far enough from either midnight that a DST
  // shift cannot move the calendar date.
  if (!closingTime) return dayjs.tz(`${closingDay}T12:00:00`, zone);

  const clock =
    source === 'persisted' ? dayjs(closingTime).tz(zone) : dayjs(closingTime);
  return dayjs.tz(`${closingDay}T${clock.format('HH:mm:ss')}`, zone);
};

/**
 * Formats a contract-anchored date (binding agreement date, closing date)
 * for short display ("Aug 27"), reading it in the property's timezone
 * rather than the viewer's. The backend normalizes these to 23:59:59.999 in
 * the property's timezone (see parseContractDate), so formatting with the
 * browser's local timezone can roll the calendar date forward a day for a
 * buyer east of the property (e.g. an Eastern-time buyer on an AL home).
 */
export const formatContractDateShort = (
  date: string | Date,
  propertyTimezone: string
): string => dayjs(date).tz(propertyTimezone).format('MMM D');

/**
 * Combines closing date and time into a timezone-aware dayjs object
 * @param closeDate - The closing date (can be string or Date)
 * @param closingTime - Optional closing time (can be string or Date)
 * @param userTimezone - Optional user timezone (defaults to Central Time)
 * @returns dayjs object in the specified timezone
 */
export const combineClosingDateTime = (
  closeDate: string | Date,
  closingTime?: string | Date | null,
  userTimezone = DEFAULT_TIMEZONE
) =>
  atClosingTime(
    dayjs(closeDate).utc().format('YYYY-MM-DD'),
    closingTime,
    userTimezone,
    'persisted'
  );

/**
 * Calculates days until closing (can be negative if past)
 * @param closingDateTime - dayjs object representing the closing date/time
 * @returns number of days until closing
 */
export const calculateDaysToClose = (closingDateTime: dayjs.Dayjs): number => {
  const centralTimeZone = 'America/Chicago';
  const currentDateCT = dayjs().tz(centralTimeZone);

  return closingDateTime
    .startOf('day')
    .diff(currentDateCT.startOf('day'), 'day');
};

/**
 * The one closing date every surface should read: the day from the
 * materialized CLOSING deadline (falling back to the base offer), read in the
 * property's timezone and pinned to the agreed closing time.
 *
 * `userTimezone` is now only the fallback for a transaction carrying no
 * listing — see {@link ClosingDateSource}. `propertyTimezone` is the
 * coordinate-resolved zone (e.g. from `usePropertyTimezone`); pass it
 * whenever the caller has one, since it corrects the state-map's wrong
 * answer for a straddle state like Tennessee.
 */
export const getTransactionClosingDate = (
  transaction: ClosingDateSource | null | undefined,
  userTimezone = DEFAULT_TIMEZONE,
  propertyTimezone?: string
): dayjs.Dayjs | null => {
  const zone = resolveClosingTimezone(
    transaction,
    userTimezone,
    propertyTimezone
  );
  const closingDay = resolveClosingDay(transaction, zone);
  if (!closingDay) {
    return null;
  }

  return atClosingTime(closingDay, transaction?.closingTime, zone, 'persisted');
};

/**
 * Return the canonical property-zoned closing date for compact admin readers.
 * Contract dates are persisted at the property's end of day, so callers must
 * format this Dayjs value in its property zone rather than the viewer's zone.
 */
export const getTransactionClosingDateForAdmin = (
  transaction: ClosingDateSource | null | undefined,
  userTimezone = DEFAULT_TIMEZONE
): dayjs.Dayjs | null => {
  const hasClosingDeadline = transaction?.deadlines?.some(
    (deadline) => deadline.type === 'CLOSING'
  );
  const hasStandaloneAppointment = !transaction?.closingDate && Boolean(transaction?.closingTime);
  const canonicalDate = hasClosingDeadline || transaction?.closingDate || !hasStandaloneAppointment
    ? getTransactionClosingDate(transaction, userTimezone)
    : null;

  if (canonicalDate?.isValid()) {
    return canonicalDate;
  }

  const value = getTransactionClosingValue(transaction);
  if (!value) return null;

  const parsed = dayjs(value);
  if (!parsed.isValid()) return null;

  const zone = resolveClosingTimezone(transaction, userTimezone);
  return parsed.tz(zone);
};

/** Calendar-day distance from now in the property's timezone. */
export const getTransactionClosingDaysAway = (
  transaction: ClosingDateSource | null | undefined,
  now = dayjs()
): number | null => {
  const closingDate = getTransactionClosingDateForAdmin(transaction);
  if (!closingDate) return null;
  const zone = resolveClosingTimezone(transaction, DEFAULT_TIMEZONE);
  return closingDate.startOf('day').diff(dayjs(now).tz(zone).startOf('day'), 'day');
};

/**
 * The closing day paired with a time the customer has just picked, for the
 * closing-time form. Resolves the day the same way as everything else, so the
 * time is written against the date the rest of the page shows rather than the
 * base offer's possibly-superseded one.
 *
 * `propertyTimezone`, when supplied, overrides the state-map guess the same
 * way it does in {@link getTransactionClosingDate} — this is the path that
 * writes a customer's picked time back to the API, so a straddle-state miss
 * here is what silently stores a chosen 2pm closing as 3pm.
 */
export const combineTransactionClosingTime = (
  transaction: ClosingDateSource | null | undefined,
  closingTime: string | Date | null | undefined,
  userTimezone = DEFAULT_TIMEZONE,
  propertyTimezone?: string
): dayjs.Dayjs | null => {
  const zone = resolveClosingTimezone(
    transaction,
    userTimezone,
    propertyTimezone
  );
  const closingDay = resolveClosingDay(transaction, zone);
  if (!closingDay) {
    return null;
  }
  // 'picked': the value is straight off the time picker, so its local clock
  // fields are the wall-clock time the customer chose.
  return atClosingTime(closingDay, closingTime, zone, 'picked');
};

/**
 * Returns true when the transaction actually closed (`closing === 'COMPLETED'`)
 * and its closing date is more than `thresholdDays` in the past. Used to hide
 * long-closed transactions from dashboard surfaces.
 *
 * A passed closing date alone is never enough — an unconfirmed transaction
 * (closing still NONE/SCHEDULED) must stay visible as overdue, not vanish
 * from the customer's dashboard the same way ClosingDate.tsx no longer treats
 * a passed date as "Closed" on its own (see checkOverdueTransactionClosings.ts
 * for how an overdue-unconfirmed deal gets flagged for a human).
 *
 * No `propertyTimezone` parameter: this runs over whole lists of transactions
 * at once (see Transactions.tsx), one per listing, so there is no single
 * coordinate-resolved zone to pass in without resolving every listing's
 * timezone up front for a filter that only cares about a 30-day threshold —
 * a state-map miss of an hour never flips that comparison.
 */
export const isTransactionStaleClosed = (
  transaction: ClosingDateSource | null | undefined,
  thresholdDays = 30
): boolean => {
  if (transaction?.closing !== 'COMPLETED') {
    return false;
  }
  const closingDate = getTransactionClosingDate(transaction);
  if (!closingDate) {
    return false;
  }
  return calculateDaysToClose(closingDate) < -thresholdDays;
};

/**
 * Gets the closing date/time as a formatted string
 * @param transaction - Transaction object with offer and closingTime
 * @param userTimezone - Optional user timezone (defaults to Central Time)
 * @param includeTime - Whether to include time in the string (defaults to true if closingTime exists)
 * @param propertyTimezone - Coordinate-resolved property zone; overrides the state-map guess (see {@link getTransactionClosingDate})
 * @returns Formatted string of the closing date/time, or null if no closing date
 */
export const getTransactionClosingDateString = (
  transaction: ClosingDateSource | null | undefined,
  userTimezone = DEFAULT_TIMEZONE,
  includeTime?: boolean,
  propertyTimezone?: string
): string | null => {
  const closingDate = getTransactionClosingDate(
    transaction,
    userTimezone,
    propertyTimezone
  );

  if (!closingDate) {
    return null;
  }

  const hasTime = !!transaction?.closingTime;
  const shouldIncludeTime = includeTime ?? hasTime;

  if (shouldIncludeTime && hasTime) {
    return closingDate.format('MMMM D, YYYY [at] h:mm A');
  }

  return closingDate.format('MMMM D, YYYY');
};

/**
 * Gets both the dayjs object and formatted string for a transaction's closing
 * @param transaction - Transaction object with offer and closingTime
 * @param userTimezone - Optional user timezone (defaults to Central Time)
 * @param propertyTimezone - Coordinate-resolved property zone; overrides the state-map guess (see {@link getTransactionClosingDate})
 * @returns Object with date (dayjs), dateString, and whether time is set
 */
export const getTransactionClosingInfo = (
  transaction: ClosingDateSource | null | undefined,
  userTimezone = DEFAULT_TIMEZONE,
  propertyTimezone?: string
): {
  date: dayjs.Dayjs | null;
  dateString: string | null;
  dateOnlyString: string | null;
  timeString: string | null;
  hasTime: boolean;
} => {
  const date = getTransactionClosingDate(
    transaction,
    userTimezone,
    propertyTimezone
  );
  const hasTime = !!transaction?.closingTime;

  if (!date) {
    return {
      date: null,
      dateString: null,
      dateOnlyString: null,
      timeString: null,
      hasTime: false,
    };
  }

  return {
    date,
    dateString: hasTime
      ? date.format('MMMM D, YYYY [at] h:mm A')
      : date.format('MMMM D, YYYY'),
    dateOnlyString: date.format('MMMM D, YYYY'),
    timeString: hasTime ? date.format('h:mm A') : null,
    hasTime,
  };
};
